import { config } from "@/config.js";
import {
    CLEAR_LAST_LIST_QUERY_PARAMS,
    SET_BULLETIN_UNREAD_COUNT,
    SET_LOGGEDIN_STATUS,
    SET_OFFICE,
    SET_SELECTED_ROLE,
    SET_USER,
} from "@/mutation_types.js";

import {
    USER_LOGIN,
    USER_LOGOUT,
    USER_UPDATE_BULLETIN_UNREAD,
} from "@/action_types.js";

import LocalStorage from "@/helpers/localstorage.js";
import * as Sentry from "@sentry/vue";
import axios from "axios";

const state = {
    loggedIn: false,
    userName: "",
    email: "",
    office: {
        id: null,
        address: "",
        name: "",
        phone: "",
    },
    rememberMe: false,
    roles: [],
    selectedRole: null,
    bulletinUnreadCount: 0,
};

const getters = {
    /**
     * @param state
     * @returns {boolean}
     */
    isLoggedIn(state) {
        return state.loggedIn;
    },

    getBulletinUnreadCount(state) {
        return state.bulletinUnreadCount;
    },

    /**
     * @param state
     * @param getters
     * @returns {*}
     */
    isRoleAdmin(state, getters) {
        return getters.hasRoleAvailable(["ROLE_MTU_ADMIN"]);
    },

    /**
     * @param state
     * @param getters
     * @returns {*}
     */
    isRoleViewer(state, getters) {
        return getters.hasRoleAvailable(["ROLE_MTU_VIEWER"]);
    },

    /**
     *
     * @param state
     * @returns {function(*=): boolean}
     */
    hasRole: (state) => (role) => {
        // return state.roles.indexOf(role) > -1;
        return role === state.selectedRole;
    },

    /**
     *
     * @param state
     * @returns {function(*=): boolean}
     */
    isOnlyAdminOrSpecialist: (state) => () => {
        return state.roles.every(
            (role) => role === "ROLE_MTU_ADMIN" || role === "ROLE_OFFICE_SPECIALIST",
        );
    },

    /**
     * @param state
     * @param getters
     * @returns {function(*): boolean}
     */
    hasRoleAvailable: (state, getters) => (roleArray) => {
        return !!roleArray.some((role) => getters.hasRole(role));
    },
};

const mutations = {
    [SET_USER](state, payload) {
        state.userName = payload.userName;
        state.email = payload.email;
        state.office = payload.office
            ? payload.office
            : {
                id: null,
                address: "",
                name: "",
                phone: "",
            };
        state.roles = payload.roles;

        if (!state.roles.length)
            // No roles available
            return;

        if (
            payload.selectedRole &&
            state.roles.indexOf(payload.selectedRole) !== -1
        ) {
            // Set desired role
            state.selectedRole = payload.selectedRole;

            return;
        }

        if (state.selectedRole && state.roles.indexOf(state.selectedRole) !== -1) {
            // Already has a selected role which is still valid

            return;
        }

        state.selectedRole = state.roles[0];

        Sentry.setUser({
            username: state.userName + " (" + state.selectedRole + ")",
            email: state.email,
        });
    },

    [SET_SELECTED_ROLE](state, payload) {
        if (!state.roles.length)
            // No roles available
            return;

        if (
            payload.selectedRole &&
            state.roles.indexOf(payload.selectedRole) !== -1
        ) {
            // Set desired role
            state.selectedRole = payload.selectedRole;

            let userData = LocalStorage.getUserData();

            userData.selectedRole = state.selectedRole;

            LocalStorage.setUserData(userData);

            Sentry.setUser({
                username: state.userName + " (" + state.selectedRole + ")",
                email: state.email,
            });
        }
    },

    [SET_OFFICE](state, payload) {
        state.office = payload.office;

        if (state.rememberMe) {
            let userData = LocalStorage.getUserData();

            userData.office = state.office;

            LocalStorage.setUserData(userData);
        }
    },

    [SET_LOGGEDIN_STATUS](state, payload) {
        state.loggedIn = payload.loggedInStatus;
        state.rememberMe = payload.rememberMe;
    },

    [SET_BULLETIN_UNREAD_COUNT](state, payload) {
        state.bulletinUnreadCount = payload.count;
    },
};

const actions = {
    [USER_LOGIN]({ commit }, payload) {
        commit({
            type: SET_USER,
            userName: payload.userName,
            email: payload.email,
            office: payload.office,
            roles: payload.roles,
            selectedRole: payload.selectedRole,
        });

        commit({
            type: SET_LOGGEDIN_STATUS,
            loggedInStatus: true,
            rememberMe: payload.rememberMe,
        });

        if (payload.rememberMe) {
            LocalStorage.setUserData({
                userName: payload.userName,
                email: payload.email,
                office: payload.office,
                roles: payload.roles,
                selectedRole: payload.roles.length ? payload.roles[0] : null,
            });
        }
    },
    [USER_LOGOUT]({ commit }) {
        commit({
            type: SET_USER,
            userName: "",
            email: "",
            office: {
                id: null,
                address: "",
                name: "",
                phone: "",
            },
            roles: [],
            selectedRole: null,
        });

        commit({
            type: SET_LOGGEDIN_STATUS,
            loggedInStatus: false,
            rememberMe: false,
        });

        commit({
            type: CLEAR_LAST_LIST_QUERY_PARAMS,
            groupType: "attraction",
        });

        commit({
            type: CLEAR_LAST_LIST_QUERY_PARAMS,
            groupType: "event",
        });

        LocalStorage.deleteUserData();
        LocalStorage.deleteJwt();
    },
    [USER_UPDATE_BULLETIN_UNREAD]({ commit }, payload) {
        axios
            .get("/api/v1/companies/1/me/bulletin/news/unread")
            .then((response) => {
                commit({
                    type: SET_BULLETIN_UNREAD_COUNT,
                    count: response.data.count,
                });
            });
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};
